import { Component, Inject, inject } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { CommonModule } from "@angular/common";
import { Constants } from "@config/constants";
import { Messages } from "@config/messages";
import { ICreateEmailTemplateRequest, IEmailTemplate, IUpdateEmailTemplateRequest } from "@modals/api.modal";
import { HttpService } from "@services/http.service";
import { ToastService } from "@services/toast.service";
import { Observable } from "rxjs";

export interface EmailTemplateDialogData {
  isEdit: boolean;
  template?: IEmailTemplate;
}

@Component({
  selector: "kzn-create-email-template-dialog",
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatIconModule, MatButtonModule],
  templateUrl: "./create-email-template-dialog.component.html",
  styleUrl: "./create-email-template-dialog.component.scss",
})
export class CreateEmailTemplateDialogComponent {
  private _httpService = inject(HttpService);
  private _toastr = inject(ToastService);

  form: FormGroup;
  isEdit = false;
  isSubmitting = false;

  constructor(
    private _dialogRef: MatDialogRef<CreateEmailTemplateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EmailTemplateDialogData,
    fb: FormBuilder
  ) {
    this.isEdit = data?.isEdit ?? false;

    this.form = fb.group({
      emailTemplateName: new FormControl("", Validators.required),
      emailLogo: new FormControl("", Validators.required),
      fileName: new FormControl(""),
      emailBodyHeader: new FormControl("", Validators.required),
      emailSignature: new FormControl("", Validators.required),
    });

    if (this.isEdit && data.template) {
      this.form.patchValue({
        emailTemplateName: data.template.TemplateName,
        emailLogo: data.template.LogoImageData,
        fileName: data.template.LogoImageName,
        emailBodyHeader: data.template.EmailHeader,
        emailSignature: data.template.EmailSignature,
      });
    }
  }

  onFileSelected(event: any): void {
    const file: File = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result as string;
        this.form.patchValue({
          emailLogo: base64String?.split("base64,")[1],
          fileName: file.name,
        });
      };
      reader.readAsDataURL(file);
    }
  }

  onSubmit(): void {
    this.form.markAllAsTouched();
    if (!this.form.valid) return;

    this.isSubmitting = true;
    this.form.disable();
    const values = this.form.value;

    const request: ICreateEmailTemplateRequest | IUpdateEmailTemplateRequest = {
      ...(this.isEdit && this.data.template ? this.data.template : {}),
      LogoImageData: values.emailLogo,
      LogoImageName: values.fileName,
      EmailHeader: values.emailBodyHeader,
      EmailSignature: values.emailSignature,
      TemplateName: values.emailTemplateName,
    };

    const api$: Observable<any> = this.isEdit
      ? this._httpService.put(this._httpService.createEndpoint(Constants.API_URI_EMAIL_TEMPLATE_UPDATE), request)
      : this._httpService.post(this._httpService.createEndpoint(Constants.API_URI_EMAIL_TEMPLATE_CREATE), request);

    api$.subscribe(
      () => {
        this.isSubmitting = false;
        this.form.enable();
        this._toastr.showSuccess(
          this.isEdit ? "Email template updated successfully" : Messages.MSG_SUCCESS_CREATE_EMAIL_TEMPLATE
        );
        this._dialogRef.close(true);
      },
      (errorResponse) => {
        this.isSubmitting = false;
        this.form.enable();
        this._httpService.showHttpError(errorResponse);
      }
    );
  }

  onClose(): void {
    this._dialogRef.close(false);
  }
}
